import React, { useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const FREQ = [["monthly", "Every month"], ["6w", "Every 6 weeks"], ["quarterly", "Every 12 weeks"], ["4m", "Every 4 months"], ["6m", "Twice a year"]] as const;
const MIX = ["Mixed reds & whites", "Mostly red", "Mostly white", "Reds only", "Whites only", "Include sparkling", "Include rosé"];
const Chip = ({ on, onClick, children, testId }: { on: boolean; onClick: () => void; children: React.ReactNode; testId: string }) => (
  <button type="button" onClick={onClick} className={`border px-3 py-2 text-xs transition-colors duration-200 ${on ? "text-[var(--paper)]" : "border-border hover:border-[var(--club-dark)]"}`} style={on ? { background: "var(--club-dark)", borderColor: "var(--club-dark)" } : undefined} data-testid={testId}>{children}</button>
);

export function BespokePlanDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [basis, setBasis] = useState<"per_month" | "per_case">("per_month");
  const [budget, setBudget] = useState(60);
  const [freq, setFreq] = useState<string>("quarterly");
  const [bottles, setBottles] = useState(12);
  const [mix, setMix] = useState(MIX[0]);
  const [prefs, setPrefs] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ reference: string; summary: string } | null>(null);
  const perBottle = basis === "per_case" ? budget / bottles : null;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/club/bespoke-request", { budget_pence: Math.round(budget * 100), budget_basis: basis, frequency: freq, bottles, mix, preferences: prefs, phone });
      setDone(data);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-none" data-testid="bespoke-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium" style={{ color: "var(--club-dark)" }}>Build your own plan</DialogTitle>
          <DialogDescription>Tell us the budget, rhythm and style you'd like. Nothing is charged now — we confirm the details with you, then sets the plan up.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4" data-testid="bespoke-done">
            <p className="font-serif text-2xl">Thank you — we've got it.</p>
            <p className="text-sm text-neutral-700">Your plan: <strong>{done.summary}</strong>. Reference <strong>{done.reference}</strong>. We'll be in touch within one working day to confirm and set it up.</p>
            <Button className="rounded-none" onClick={onClose} data-testid="bespoke-close">Done</Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <Label className="eyebrow">Your budget</Label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <div className="flex items-center border border-border"><span className="px-3 text-neutral-500">£</span><Input type="number" min={30} max={5000} step={5} value={budget} onChange={(e) => setBudget(Number(e.target.value))} className="w-28 rounded-none border-0 bg-transparent" data-testid="bespoke-budget" /></div>
                <Chip on={basis === "per_month"} onClick={() => setBasis("per_month")} testId="bespoke-basis-month">per month</Chip>
                <Chip on={basis === "per_case"} onClick={() => setBasis("per_case")} testId="bespoke-basis-case">per case</Chip>
                {perBottle !== null && <span className="text-xs text-neutral-500">≈ £{perBottle.toFixed(2)} a bottle</span>}
              </div>
              <input type="range" min={30} max={500} step={5} value={Math.min(budget, 500)} onChange={(e) => setBudget(Number(e.target.value))} className="mt-3 w-full accent-[var(--club-dark)]" data-testid="bespoke-budget-slider" />
            </div>
            <div>
              <Label className="eyebrow">How often</Label>
              <div className="mt-2 flex flex-wrap gap-2">{FREQ.map(([k, l]) => <Chip key={k} on={freq === k} onClick={() => setFreq(k)} testId={`bespoke-freq-${k}`}>{l}</Chip>)}</div>
            </div>
            <div>
              <Label className="eyebrow">Bottles per case</Label>
              <div className="mt-2 flex flex-wrap gap-2">{[6, 12, 18, 24].map((n) => <Chip key={n} on={bottles === n} onClick={() => setBottles(n)} testId={`bespoke-bottles-${n}`}>{n}</Chip>)}<div className="flex items-center border border-border"><Input type="number" min={3} max={60} value={bottles} onChange={(e) => setBottles(Number(e.target.value))} className="w-20 rounded-none border-0 bg-transparent" data-testid="bespoke-bottles-custom" /><span className="pr-3 text-xs text-neutral-500">other</span></div></div>
            </div>
            <div>
              <Label className="eyebrow">Style</Label>
              <div className="mt-2 flex flex-wrap gap-2">{MIX.map((m) => <Chip key={m} on={mix === m} onClick={() => setMix(m)} testId={`bespoke-mix-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{m}</Chip>)}</div>
            </div>
            <div className="grid gap-4 sm:grid-cols-[1fr_180px]">
              <div><Label className="eyebrow">Anything we should know?</Label><Textarea value={prefs} onChange={(e) => setPrefs(e.target.value)} rows={3} maxLength={1500} placeholder="Regions you love, grapes to avoid, occasions, delivery notes…" className="mt-2 rounded-none bg-transparent text-sm" data-testid="bespoke-prefs" /></div>
              <div><Label className="eyebrow">Phone (optional)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2 rounded-none bg-transparent" data-testid="bespoke-phone" /></div>
            </div>
            <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-neutral-500" data-testid="bespoke-summary">£{budget} {basis === "per_month" ? "a month" : "per case"} · {bottles} bottles {FREQ.find(([k]) => k === freq)?.[1].toLowerCase()} · {mix.toLowerCase()}</p>
              <Button className="w-full rounded-none sm:w-auto" style={{ background: "var(--club-dark)" }} disabled={busy} onClick={submit} data-testid="bespoke-submit">{busy ? "Sending…" : "Send my plan to the family"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
